import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';

/** Matches the backend's role enum: vendors are the only users who sign in. */
export type Role = 'VENDOR';

export interface RouteDefinition {
  /** Path relative to the app root, without a leading slash. */
  path: string;
  /** Sidebar label. Absent means the route is reachable but not listed (a detail page). */
  label?: string;
  /** Icon name resolved by the sidebar; keeps MUI icon imports out of this file. */
  icon?: string;
  /** Sidebar group heading. */
  group?: string;
  /** Route-level code splitting, so AG Grid is not in the initial bundle. */
  component: LazyExoticComponent<ComponentType>;
}

/** Every screen behind sign-in. The customer's order chat is public and lives in AppRouter. */
export const routes: RouteDefinition[] = [
  {
    path: 'dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    group: 'Overview',
    component: lazy(() => import('@/features/dashboard/pages/DashboardPage')),
  },
  {
    path: 'orders',
    label: 'Orders',
    icon: 'receipt_long',
    group: 'Overview',
    component: lazy(() => import('@/features/orders/pages/OrdersPage')),
  },
  {
    path: 'customers',
    label: 'Customers',
    icon: 'groups',
    group: 'Overview',
    component: lazy(() => import('@/features/customers/pages/CustomersPage')),
  },
  {
    // Opened from a Customers row: the customer and their orders.
    path: 'customers/:customerId',
    component: lazy(() => import('@/features/customers/pages/CustomerPage')),
  },
];

export const DEFAULT_ROUTE = '/dashboard';

/** Sidebar groups in declaration order, with their visible routes. */
export interface NavGroup {
  name: string;
  routes: RouteDefinition[];
}

export function buildNavigation(allRoutes: RouteDefinition[]): NavGroup[] {
  const groups = new Map<string, RouteDefinition[]>();
  for (const route of allRoutes) {
    if (!route.label) {
      continue;
    }
    const groupName = route.group ?? 'Other';
    groups.set(groupName, [...(groups.get(groupName) ?? []), route]);
  }
  return Array.from(groups, ([name, groupRoutes]) => ({ name, routes: groupRoutes }));
}
